/**
 * chatModeRules — where the chat may be on screen, and where it goes when it
 * is minimized (Miles's ask, relayed 2026-10-08; Justin's rulings the same day).
 *
 * THE ASK. "It never half opens in the center anymore … when the user opens up
 * the chat interface from the bottom center it should automatically snap the
 * chat interface to the right … then the user can open up the chat interface
 * full screen … and should be able to minimize to bottom center or bottom
 * [right], whichever was last, or another button for align right (which should
 * be more primary of an option than minimize)."
 *
 * So on a DESKTOP an open chat has exactly two shapes: the docked right rail,
 * or full screen. The centred floating pane that sat between them is gone.
 * That is one sentence, and it lives here once, as `normalizeChatMode`, which
 * the reducer applies after every mode action and the boot read applies to the
 * first frame — so no dispatch site, present or future (a pill tap, a concierge
 * `forceDocked`, the same-tab mirror, a stored pre-2026-10-08 floating state),
 * can produce the old pane. On a desktop the dock flag is therefore DERIVED:
 * docked exactly when not full screen. A phone keeps its own rules (open is the
 * full-screen takeover; it never docks), untouched.
 *
 * THE MINIMIZED SPOT. Closed, the chat rests in one of two places: the bar at
 * bottom CENTER (where a new device starts) or the compact bar at bottom RIGHT,
 * under where the rail was (Justin: never the left — Miles's 2026-08-20 rule
 * keeps the chat on the right). Minimizing the docked rail tucks it in place
 * (right); minimizing from full screen returns it to the spot it was opened
 * from, which is the spot it last rested in — that is the "whichever was last".
 *
 * This file is the ONE owner of that vocabulary ('center' | 'right'): anything
 * else read back, "left" included, parses as center.
 *
 * Leaf only: it imports the storage helpers and the keys, never the engine, so
 * the boot read (bootChatMode.ts) and the onboarding redirect can use it from
 * the first download (scripts/check-chat-first-paint.mjs).
 */
import { MOBILE_VIEWPORT_QUERY } from '../../styles/breakpoints';
import {
  CHAT_MODE_TAB_KEY,
  GLOBAL_CHAT_DOCK_KEY,
  GLOBAL_CHAT_MINIMIZED_SPOT_KEY,
  GLOBAL_CHAT_OPEN_KEY,
} from './types';
import { getFromLocalStorage, setToLocalStorage } from './utils';

/** Where a closed chat rests on a desktop. */
export type ChatMinimizedSpot = 'center' | 'right';

/** Every spot, for tests that walk the whole vocabulary. */
export const CHAT_MINIMIZED_SPOTS: readonly ChatMinimizedSpot[] = ['center', 'right'];

/** A stored or received value, read as a spot. Unknown (and "left") ⇒ center. */
export function parseMinimizedSpot(value: unknown): ChatMinimizedSpot {
  return value === 'right' ? 'right' : 'center';
}

/** The mode flags the rule reads — a subset of the reducer's state. */
export interface ChatModeFlags {
  isChatOpen: boolean;
  isChatDockedRight: boolean;
  isChatFullScreen: boolean;
  isMobileViewport: boolean;
}

/**
 * THE rule: on a desktop the chat is docked exactly when it is not full
 * screen — open or closed. (Closed, the pane keeps the rail's geometry, so
 * minimizing fades the rail out in place and the next open fades it in at the
 * right.) A phone is returned untouched; its own reducer guards already keep it
 * undocked. Returns the SAME object when nothing changes, so a reducer that
 * applies it never manufactures a new state for a no-op.
 */
export function normalizeChatMode<T extends ChatModeFlags>(flags: T): T {
  if (flags.isMobileViewport) return flags;
  const docked = !flags.isChatFullScreen;
  return flags.isChatDockedRight === docked ? flags : { ...flags, isChatDockedRight: docked };
}

/**
 * Where minimize leaves the chat. The docked rail tucks in place (right);
 * full screen goes back to where it was opened from (the spot it last rested
 * in, unchanged); a phone has no spots (its launcher is its own), so the
 * desktop choice is left exactly as it was.
 */
export function spotAfterMinimize(args: {
  fullScreen: boolean;
  phone: boolean;
  spot: ChatMinimizedSpot;
}): ChatMinimizedSpot {
  if (args.phone || args.fullScreen) return args.spot;
  return 'right';
}

/**
 * The stored spot. The first read on a device that has none derives it ONCE
 * from the layout it already had — a chat closed while docked sat at bottom
 * right — and writes it straight away, so later dock-flag writes (every open
 * now writes docked) can never re-derive it wrongly.
 */
export function readMinimizedSpot(): ChatMinimizedSpot {
  if (typeof window === 'undefined') return 'center';
  const stored = getFromLocalStorage<string>(GLOBAL_CHAT_MINIMIZED_SPOT_KEY, '');
  if (stored) return parseMinimizedSpot(stored);
  const derived = peekMinimizedSpot();
  setToLocalStorage(GLOBAL_CHAT_MINIMIZED_SPOT_KEY, derived);
  return derived;
}

/** The same answer, recording nothing: for a read that must leave the device's default as it is (chatModeMemory.ts). */
export function peekMinimizedSpot(): ChatMinimizedSpot {
  if (typeof window === 'undefined') return 'center';
  const stored = getFromLocalStorage<string>(GLOBAL_CHAT_MINIMIZED_SPOT_KEY, '');
  if (stored) return parseMinimizedSpot(stored);
  const wasDocked = getFromLocalStorage<string>(GLOBAL_CHAT_DOCK_KEY, '0') === '1';
  const wasOpen = getFromLocalStorage<string>(GLOBAL_CHAT_OPEN_KEY, '0') === '1';
  return wasDocked && !wasOpen ? 'right' : 'center';
}

/** Record where the chat now rests. */
export function persistMinimizedSpot(spot: ChatMinimizedSpot): void {
  if (typeof window === 'undefined') return;
  setToLocalStorage(GLOBAL_CHAT_MINIMIZED_SPOT_KEY, parseMinimizedSpot(spot));
}

/** Is this a phone-sized viewport right now? (No media query support ⇒ a desktop.) */
function phoneViewport(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * Does a person's first arrival open the chat on its own? Only on a phone,
 * where the welcome has always opened as the full-screen takeover ("keep what
 * it is now for mobile"). On a desktop the first arrival lands on the web
 * application with the bar at the bottom (Miles, 2026-10-08).
 */
export function arrivalOpensChat(): boolean {
  return phoneViewport();
}

/**
 * A person's first arrival on the app lands on the page, not on an open chat
 * (Miles, 2026-10-08: "the initial state when a user signs in for the first
 * time should be the web application"). Onboarding ends with a full page load,
 * so recording the chat as closed before it is the whole mechanism: the boot
 * read then paints the page with the bar at the bottom, even on a device where
 * a visitor had the chat open on the home page before signing up. A phone is
 * left alone — its first arrival still opens the welcome (keep what it is now
 * for mobile).
 */
export function landOnThePageOnArrival(): void {
  if (typeof window === 'undefined' || phoneViewport()) return;
  setToLocalStorage(GLOBAL_CHAT_OPEN_KEY, '0');
  // The tab keeps its own memory across the full page load (chatModeMemory.ts, 2026-10-15) and reads it
  // first: forget it, so this arrival boots from the device's default just written.
  try {
    window.sessionStorage.removeItem(CHAT_MODE_TAB_KEY);
  } catch {
    // no tab memory to forget
  }
}
